import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

import { MONITORING_YEARS, MONTHS_TO_HANDOVER, PROCESS_STAGES } from '@/constants/process'

/**
 * One bar for a whole commission: the five stages to handover at their real length in months, a
 * break, then ten years of monitoring drawn at a compressed scale with a tick per year.
 */
export const ProcessSummary: FC = (): ReactNode => {
    const [ref, inView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })
    const build = PROCESS_STAGES.filter(stage => stage.months !== null)

    let elapsed = 0

    return (
        <div ref={ref} data-reveal={inView ? 'in' : 'pre'}>
            <div className='grid gap-4 desk:grid-cols-[minmax(0,3fr)_2.5rem_minmax(0,2fr)]'>
                <div>
                    <p className='hud flex justify-between text-mute'>
                        <span>
                            To handover
                        </span>

                        <span className='text-ink'>
                            About {MONTHS_TO_HANDOVER} months
                        </span>
                    </p>

                    <div className='mt-3 flex h-10 gap-px'>
                        {build.map((stage, index) => {
                            const start = elapsed

                            elapsed += stage.months ?? 0

                            return (
                                <div
                                    key={stage.id}
                                    className='relative h-full'
                                    style={{ width: `${((stage.months ?? 0) / MONTHS_TO_HANDOVER) * 100}%` }}
                                    title={`${stage.title}, ${stage.duration}`}
                                >
                                    <span
                                        className={cn('draw-x absolute inset-0', index === build.length - 1 ? 'bg-signal' : 'bg-ink/80')}
                                        style={{ '--delay': `${index * 160}ms` } as CSSProperties}
                                        aria-hidden='true'
                                    />

                                    <span className='sr-only'>
                                        {stage.title}: month {start + 1} to month {elapsed}
                                    </span>
                                </div>
                            )
                        })}
                    </div>

                    <div className='mt-3 flex gap-px' aria-hidden='true'>
                        {build.map(stage => (
                            <span key={stage.id} className='hud min-w-0 truncate text-mute' style={{ width: `${((stage.months ?? 0) / MONTHS_TO_HANDOVER) * 100}%` }}>
                                {stage.number}
                            </span>
                        ))}
                    </div>
                </div>

                <div className='hidden items-center justify-center desk:flex' aria-hidden='true'>
                    <svg viewBox='0 0 24 48' className='h-12 w-6'>
                        <path d='M6 4 L14 24 L6 44 M12 4 L20 24 L12 44' fill='none' stroke='#8A8478' strokeWidth={1} />
                    </svg>
                </div>

                <div>
                    <p className='hud flex justify-between text-mute'>
                        <span>
                            Monitoring
                        </span>

                        <span className='text-ink'>
                            {MONITORING_YEARS} years
                        </span>
                    </p>

                    <div className='relative mt-3 h-10 border border-signal/70'>
                        <span className='draw-x absolute inset-0 bg-signal/15' style={{ '--delay': '900ms' } as CSSProperties} aria-hidden='true' />

                        {Array.from({ length: MONITORING_YEARS - 1 }, (_, index) => (
                            <span key={index} className='absolute inset-y-0 w-px bg-signal/50' style={{ left: `${((index + 1) / MONITORING_YEARS) * 100}%` }} aria-hidden='true' />
                        ))}
                    </div>

                    <div className='mt-3 flex justify-between' aria-hidden='true'>
                        <span className='hud text-mute'>
                            Year 1
                        </span>

                        <span className='hud text-mute'>
                            Year {MONITORING_YEARS}
                        </span>
                    </div>
                </div>
            </div>

            <p className='hud mt-8 text-mute'>
                Bar lengths to handover are to scale in months. Monitoring is compressed: each cell is one year.
            </p>
        </div>
    )
}
